import type { App, ProceduralBeat } from "@engine";
import type { Shape } from "../level/types";
import type { HUD } from "../ui/HUD";
import { shapeOf } from "../config/shapes";

// Tight, punchy gate transition. The slow-mo dip is intentionally light
// (0.7x for ~0.1s) so the player keeps control through the morph while
// still feeling a beat-aligned "thump". Total real-time duration ~0.32s.
//
// FOV_PEAK and DOLLY_PEAK are intentionally 0: any camera punch-in or
// FOV widen during the transition reads visually as the player ball
// "jumping" forward, which is disorienting on a gate. The bloom, gate
// emissive flare, chromatic aberration, screen flash and beat drop are
// enough to sell the morph without moving the camera.
const DURATION_S = 0.32;
const ATTACK_END = 0.25;
const HOLD_END = 0.55;
const TIME_SCALE_LOW = 0.7;
// Bloom + emissive peaks are intentionally subdued: with the new
// composer-driven music + per-theme palettes, the visual transition is
// already very busy. A modest highlight reads more cinematic than a
// flashbang.
const BLOOM_PEAK = 0.25;
const GATE_EMISSIVE_PEAK = 0.55;
const CA_PEAK = 0.01;
const FOV_PEAK = 0;
const DOLLY_PEAK = 0;

// Cinematic punctuation for shape changes. Drives a 0.7s real-time
// timeline (attack -> hold -> release) using app.time.unscaledDt so the
// transition itself never gets slowed by its own time-scale dip.
//
// GateTransition does not directly write to FOV / dolly / bloom / CA.
// Instead it exposes its current boost values; MorphHexGame.update reads
// them and composes them with the per-frame baselines from the audio
// energy / speed mapping. This keeps the responsibilities clean and
// avoids fighting other writers (e.g. FovTween) for the same field.
export class GateTransition {
  bloomBoost = 0;
  gateBoost = 0;
  caBoost = 0;
  fovBoost = 0;
  dollyBoost = 0;

  private app: App;
  private hud: HUD;
  private active = false;
  private elapsed = 0;

  // We accept `_beat` for backward-compat with the existing call site
  // even though gate transitions no longer touch the drum scheduler --
  // see trigger() for why dropping silenceFor() matters.
  constructor(app: App, _beat: ProceduralBeat, hud: HUD) {
    this.app = app;
    this.hud = hud;
  }

  trigger(toShape: Shape): void {
    this.active = true;
    this.elapsed = 0;
    this.hud.flash("gate", hexColor(shapeOf(toShape).primary));
    // Volume duck on the music bus instead of stopping the beat.
    // silenceFor() would reset the drum scheduler to step 0, which
    // makes the Composer (subscribed to onStep) think a new bar started
    // mid-bar and desync the chord progression. A bus duck gives the
    // same "kick re-enters with punch" feel without breaking the clock.
    const ctx = this.app.audio.context;
    if (ctx) {
      this.app.audio.scheduleDuck(
        ctx.currentTime,
        /*depth=*/ 0.35,
        /*attackMs=*/ 30,
        /*releaseMs=*/ 240
      );
    }
  }

  reset(): void {
    if (this.active) this.app.time.scale = 1;
    this.active = false;
    this.elapsed = 0;
    this.bloomBoost = 0;
    this.gateBoost = 0;
    this.caBoost = 0;
    this.fovBoost = 0;
    this.dollyBoost = 0;
  }

  update(unscaledDt: number): void {
    if (!this.active) return;
    this.elapsed += unscaledDt;
    if (this.elapsed >= DURATION_S) {
      this.app.time.scale = 1;
      this.active = false;
      this.bloomBoost = 0;
      this.gateBoost = 0;
      this.caBoost = 0;
      this.fovBoost = 0;
      this.dollyBoost = 0;
      return;
    }
    const norm = this.elapsed / DURATION_S;
    let amp: number;
    let timeScale: number;
    if (norm < ATTACK_END) {
      const k = norm / ATTACK_END;
      amp = k;
      timeScale = 1 + (TIME_SCALE_LOW - 1) * k;
    } else if (norm < HOLD_END) {
      amp = 1;
      timeScale = TIME_SCALE_LOW;
    } else {
      const k = (norm - HOLD_END) / (1 - HOLD_END);
      amp = 1 - k;
      timeScale = TIME_SCALE_LOW + (1 - TIME_SCALE_LOW) * k;
    }
    this.app.time.scale = timeScale;
    this.bloomBoost = BLOOM_PEAK * amp;
    this.gateBoost = GATE_EMISSIVE_PEAK * amp;
    this.caBoost = CA_PEAK * amp;
    this.fovBoost = FOV_PEAK * amp;
    this.dollyBoost = DOLLY_PEAK * amp;
  }
}

function hexColor(n: number): string {
  return `#${n.toString(16).padStart(6, "0")}`;
}
